import { ReloadOutlined } from '@ant-design/icons';
import { removeDiacritics } from '@mahawi/eshop-common/src/remove-diacritics';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { type IWarehouseCategory } from '@mahawi/eshop-common/src/types';
import { Button, Divider, Space, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Link from '../components/link';
import Table from '../components/table';
import UpdatedAt from '../components/updated-at';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  downloaderLoadWarehouseCategories,
  downloaderLoadWarehouseCategoriesUnpaired,
} from '../reducers/downloader/reducer';
import { SPACING } from '../theme/tokens';

const { Title } = Typography;

interface DataType extends IWarehouseCategory {
  key: string;
  nameNormalized: string;
}

function mapCategories(
  categories: IWarehouseCategory[] | undefined,
): DataType[] {
  return (categories ?? []).map((category: IWarehouseCategory): DataType => ({
    key: category.uuid,
    ...category,
    nameNormalized: removeDiacritics(category.name.toLowerCase().trim()),
  }));
}

function buildNameColumn(width: string) {
  return {
    title: 'Name',
    dataIndex: 'name',
    sorter: (a: DataType, b: DataType): number =>
      stringCompare(a.rawName, b.rawName),
    render: (_text: unknown, record: DataType): React.ReactElement => (
      <Link
        to={`/downloader/warehouse-category/${record.uuid}`}
        label={record.name}
        openInNewTab
      />
    ),
    width,
    ...getColumnSearch('name'),
  };
}

const actionsColumn = {
  title: 'Actions',
  sorter: (a: DataType, b: DataType): number =>
    stringCompare(a.category?.uuid, b.category?.uuid),
  render: (_text: unknown, record: DataType): React.ReactElement => (
    <Space size={SPACING.lg} align="start">
      {record.category?.uuid ? (
        <Link to={`/category/${record.category.uuid}`} openInNewTab />
      ) : null}
    </Space>
  ),
  width: '10%',
};

const columnsPaired = [
  buildNameColumn('90%'),
  actionsColumn,
] as ColumnsType<object>;
const columnsUnpaired = [buildNameColumn('100%')] as ColumnsType<object>;

function DownloaderWarehouseCategories(): React.ReactElement {
  const dispatch = useAppDispatch();
  const downloaderWarehouseCategories = useSelector(
    (s: RootState) => s.Downloader.warehouse.categories,
  );
  const downloaderWarehouseCategoriesUnpaired = useSelector(
    (s: RootState) => s.Downloader.warehouse.categoriesUnpaired,
  );
  const downloaderInProcess = useSelector(
    (s: RootState) => s.Downloader.inProcess,
  );
  const downloaderUpdatedAt = useSelector(
    (s: RootState) => s.Downloader.updatedAt,
  );

  const dataSource = useMemo(
    () => mapCategories(downloaderWarehouseCategories),
    [downloaderWarehouseCategories],
  );

  const dataSourceUnpaired = useMemo(
    () => mapCategories(downloaderWarehouseCategoriesUnpaired),
    [downloaderWarehouseCategoriesUnpaired],
  );

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <Space
        size={SPACING.lg}
        align="center"
        separator={<Divider orientation="vertical" />}
      >
        <Title level={4} style={{ margin: 0 }}>
          Paired categories
        </Title>

        <Button
          type={!downloaderWarehouseCategories?.length ? 'primary' : 'default'}
          onClick={(): void => {
            dispatch(downloaderLoadWarehouseCategories());
          }}
          disabled={downloaderInProcess}
        >
          Reload all <ReloadOutlined />
        </Button>

        <UpdatedAt value={downloaderUpdatedAt} />
      </Space>

      <Table
        columns={columnsPaired}
        dataSource={dataSource}
        isLoading={downloaderInProcess}
      />

      <Divider />

      <Space
        size={SPACING.lg}
        align="center"
        separator={<Divider orientation="vertical" />}
      >
        <Title level={4} style={{ margin: 0 }}>
          Unpaired categories with paired products
        </Title>

        <Button
          type={
            !downloaderWarehouseCategoriesUnpaired?.length
              ? 'primary'
              : 'default'
          }
          onClick={(): void => {
            dispatch(downloaderLoadWarehouseCategoriesUnpaired());
          }}
          disabled={downloaderInProcess}
        >
          Reload <ReloadOutlined />
        </Button>

        <UpdatedAt value={downloaderUpdatedAt} />
      </Space>

      <Table
        columns={columnsUnpaired}
        dataSource={dataSourceUnpaired}
        isLoading={downloaderInProcess}
      />
    </Space>
  );
}

export default DownloaderWarehouseCategories;
